import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import draftService from '../../services/draftService'
import ConfirmModal from '../ConfirmModal'
import { toast } from 'react-hot-toast'
import './DraftsList.css'

function DraftsList ({ onLoadDraft, onRefresh, currentDraftId = null }) {
  const { user } = useAuth()
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(() => {
    // Lade gespeicherten Status aus localStorage, Standard: true
    const saved = localStorage.getItem('draftsListExpanded')
    return saved !== null ? saved === 'true' : true
  })
  const [deletingId, setDeletingId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, draftId: null })

  const loadDrafts = useCallback(async () => {
    if (!user) return

    setLoading(true)
    try {
      const result = await draftService.listDrafts()
      if (result.success) {
        // Sortiere Entwürfe nach updatedAt (neueste zuerst)
        const sortedDrafts = [...(result.drafts || [])].sort((a, b) => {
          const getDate = (draft) => {
            if (!draft.updatedAt) return new Date(0)
            // Handle Firestore Timestamp with toDate() method
            if (draft.updatedAt && typeof draft.updatedAt.toDate === 'function') {
              return draft.updatedAt.toDate()
            }
            // Handle Firestore Timestamp with toMillis() method
            if (draft.updatedAt && typeof draft.updatedAt.toMillis === 'function') {
              return new Date(draft.updatedAt.toMillis())
            }
            // Handle Firestore Timestamp object with seconds property
            if (draft.updatedAt && typeof draft.updatedAt.seconds === 'number') {
              return new Date(draft.updatedAt.seconds * 1000 + (draft.updatedAt.nanoseconds || 0) / 1000000)
            }
            // Handle Firestore Timestamp object with _seconds property
            if (draft.updatedAt && typeof draft.updatedAt._seconds === 'number') {
              return new Date(draft.updatedAt._seconds * 1000 + (draft.updatedAt._nanoseconds || 0) / 1000000)
            }
            // Handle JavaScript Date object
            if (draft.updatedAt instanceof Date) {
              return draft.updatedAt
            }
            // Handle Unix timestamp (number in milliseconds)
            if (typeof draft.updatedAt === 'number') {
              return new Date(draft.updatedAt)
            }
            // Handle ISO string
            if (typeof draft.updatedAt === 'string') {
              return new Date(draft.updatedAt)
            }
            return new Date(draft.updatedAt)
          }
          
          const dateA = getDate(a)
          const dateB = getDate(b)
          
          // Neueste zuerst
          return dateB - dateA
        })
        setDrafts(sortedDrafts)
      }
    } catch (error) {
      console.error('Error loading drafts:', error)
      toast.error('Fehler beim Laden der Entwürfe')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadDrafts()
  }, [loadDrafts])

  // Expose loadDrafts function to parent via callback
  useEffect(() => {
    if (onRefresh) {
      // Store the refresh function
      onRefresh.current = loadDrafts
    }
  }, [onRefresh, loadDrafts])

  // Speichere Kollaps-Status in localStorage
  useEffect(() => {
    localStorage.setItem('draftsListExpanded', expanded.toString())
  }, [expanded])

  const handleDeleteClick = (draftId, e) => {
    e.stopPropagation()
    setConfirmDelete({ isOpen: true, draftId })
  }

  const handleDeleteConfirm = async () => {
    const draftId = confirmDelete.draftId
    if (!draftId) return

    setDeletingId(draftId)
    setConfirmDelete({ isOpen: false, draftId: null })
    
    try {
      const result = await draftService.deleteDraft(draftId)
      if (result.success) {
        toast.success('Entwurf gelöscht')
        // Remove from list
        setDrafts(prev => prev.filter(d => d.id !== draftId))
      } else {
        toast.error('Fehler beim Löschen')
      }
    } catch (error) {
      console.error('Error deleting draft:', error)
      toast.error('Fehler beim Löschen: ' + (error.message || 'Unbekannter Fehler'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleLoadDraft = async draft => {
    if (onLoadDraft) {
      onLoadDraft(draft)
    }
  }

  const formatDate = date => {
    if (!date) return 'Unbekannt'
    
    let d = null
    
    try {
      // Handle Firestore Timestamp with toDate() method
      if (date && typeof date.toDate === 'function') {
        d = date.toDate()
      }
      // Handle Firestore Timestamp with toMillis() method
      else if (date && typeof date.toMillis === 'function') {
        d = new Date(date.toMillis())
      }
      // Handle Firestore Timestamp object with seconds property
      else if (date && typeof date.seconds === 'number') {
        d = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000)
      }
      // Handle Firestore Timestamp object with _seconds property (alternative format)
      else if (date && typeof date._seconds === 'number') {
        d = new Date(date._seconds * 1000 + (date._nanoseconds || 0) / 1000000)
      }
      // Handle JavaScript Date object
      else if (date instanceof Date) {
        d = date
      }
      // Handle Unix timestamp (number in milliseconds)
      else if (typeof date === 'number') {
        d = new Date(date)
      }
      // Handle ISO string
      else if (typeof date === 'string') {
        d = new Date(date)
      }
      // Fallback: try to create Date from value
      else {
        d = new Date(date)
      }
      
      // Validate the date
      if (!d || isNaN(d.getTime())) {
        console.warn('Invalid date:', date)
        return 'Unbekannt'
      }
      
      const now = new Date()
      const diffMs = now - d
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Gerade eben'
      if (diffMins < 60) return `Vor ${diffMins} Min.`
      if (diffHours < 24) return `Vor ${diffHours} Std.`
      if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`
      return d.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Error formatting date:', error, date)
      return 'Unbekannt'
    }
  }

  if (loading) {
    return (
      <div className='drafts-list-container'>
        <div className='drafts-list-loading'>Lade Entwürfe...</div>
      </div>
    )
  }

  if (drafts.length === 0) {
    return (
      <div className='drafts-list-container'>
        <div className='drafts-list-empty'>
          <p>Noch keine Entwürfe vorhanden</p>
          <p className='drafts-list-hint'>
            Ihre Entwürfe werden automatisch gespeichert, während Sie arbeiten.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='drafts-list-container'>
      <div
        className='drafts-list-header'
        onClick={() => setExpanded(!expanded)}
      >
        <div className='drafts-list-header-content'>
          <h3>Meine Entwürfe ({drafts.length})</h3>
          <span className='drafts-list-toggle'>
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {expanded && (
        <div className='drafts-list-items'>
          {drafts.map(draft => (
            <div key={draft.id} className={`draft-item ${currentDraftId === draft.id ? 'draft-item-active' : ''}`}>
              <div
                className='draft-item-content'
                onClick={() => handleLoadDraft(draft)}
              >
                <div className='draft-item-header'>
                  <div className='draft-item-title'>
                    {draft.problemTitle || draft.solution?.substring(0, 50) + '...' || 'Unbenannter Entwurf'}
                    {draft.isQuickAdd && (
                      <span className='draft-item-badge'>Quick-Add</span>
                    )}
                    {currentDraftId === draft.id && (
                      <span className='draft-item-badge draft-item-badge-active'>Aktiv</span>
                    )}
                  </div>
                  <div className='draft-item-actions'>
                    <button
                      className='draft-item-delete'
                      onClick={e => handleDeleteClick(draft.id, e)}
                      disabled={deletingId === draft.id || currentDraftId === draft.id}
                      title={currentDraftId === draft.id ? 'Aktiver Entwurf kann nicht gelöscht werden' : 'Löschen'}
                    >
                      {deletingId === draft.id ? '...' : '×'}
                    </button>
                  </div>
                </div>
                <div className='draft-item-meta'>
                  <span className='draft-item-device'>{draft.deviceName}</span>
                  <span className='draft-item-separator'>•</span>
                  <span className='draft-item-date'>
                    {formatDate(draft.updatedAt || draft.submittedAt)}
                  </span>
                  {draft.lastSavedStep && (
                    <>
                      <span className='draft-item-separator'>•</span>
                      <span className='draft-item-step'>
                        Schritt {draft.lastSavedStep}/4
                      </span>
                    </>
                  )}
                </div>
                {draft.solution && (
                  <div className='draft-item-preview'>
                    {draft.solution.substring(0, 100)}
                    {draft.solution.length > 100 ? '...' : ''}
                  </div>
                )}
                {/* Fortschrittsindikator */}
                <div className='draft-item-progress'>
                  <div 
                    className='draft-item-progress-bar'
                    style={{ width: `${((draft.lastSavedStep || 1) / 4) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, draftId: null })}
        onConfirm={handleDeleteConfirm}
        title='Entwurf löschen'
        message='Möchten Sie diesen Entwurf wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        confirmText='Löschen'
        cancelText='Abbrechen'
        type='danger'
      />
    </div>
  )
}

export default DraftsList

